import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, Input, OnChanges, OnInit, Optional, Self, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NgControl, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-searchable-dropdown',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './searchable-dropdown.html',
  styleUrl: './searchable-dropdown.scss',
  standalone: true,
})
export class SearchableDropdown implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label = '';
  @Input() items: any[] = [];
  @Input() bindLabel = '';
  @Input() bindValue = '';
  @Input() placeholder = 'Select';
  @Input() errorMessage = 'This field is required';
  @Input() searchFields: string[] = [];
  @Input() showRequired = false;
  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef;

  showDropdown = false;
  searchText = '';
  selectedLabel = '';

  filteredItems: any[] = [];

  onChange: any = () => {};
  onTouched: any = () => {};

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private elementRef: ElementRef,
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  get invalid(): boolean {
  return !!(
    this.ngControl &&
    this.ngControl.invalid &&
    (this.ngControl.touched || this.ngControl.dirty)
  );
}


  get control() {
    return this.ngControl?.control;
  }

  get showSearch(): boolean {
    return this.items.length > 5;
  }

  ngOnInit() {
    this.filteredItems = this.items;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.filteredItems = this.items;
      // Re-resolve selectedLabel if a value is set but items changed
      const currentValue = this.ngControl?.value;
      if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
        const selected = this.items.find((i) => i[this.bindValue] === currentValue);
        this.selectedLabel = selected ? selected[this.bindLabel] : '';
      } else {
        this.selectedLabel = '';
        this.searchText = '';
      }
    }
  }

  toggleDropdown() {
    if (!this.showDropdown) {
      this.showDropdown = true;
      this.filteredItems = this.items;
      setTimeout(() => this.searchInput?.nativeElement.focus(), 0);
    } else {
      this.showDropdown = false;
    }
  }

  selectItem(item: any) {
    this.selectedLabel = item[this.bindLabel];
    this.onChange(item[this.bindValue]);
    this.showDropdown = false;
  }

  writeValue(value: any): void {
    if (value === null || value === undefined || value === '') {
      this.selectedLabel = '';
      this.searchText = '';
      this.showDropdown = false;
      return;
    }

    if (this.items.length) {
      const selected = this.items.find((i) => i[this.bindValue] === value);
      this.selectedLabel = selected ? selected[this.bindLabel] : '';
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  get filteredItemsList() {
    const needle = this.searchText?.toLowerCase?.().trim() || '';
    if (!needle) {
      return this.items;
    }
    // Use searchFields if provided, otherwise default to bindLabel
    const fieldsToSearch = this.searchFields.length > 0 ? this.searchFields : [this.bindLabel];

    return this.items.filter((item) =>
      fieldsToSearch.some((field) =>
        String(item?.[field] ?? '')
          .toLowerCase()
          .includes(needle),
      ),
    );
  }

  closeDropdown() {
    this.showDropdown = false;
  }
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickedInside = this.elementRef.nativeElement.contains(event.target);
    if (!clickedInside) {
      this.closeDropdown();
    }
  }
}
